import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import { Button, Drawer, IconButton, Stack } from '@mui/material'
// Project imports
import emptyKnowledge from '@/assets/New-images/empty-knowledge.svg'
import loadingimg from '@/assets/images/portal-loading-anim.svg'
import { LEARNING_OUTCOMES_UPDATE } from '@/config'
import '@/routes/Knowledge/AllKnowledge/AllKnowledge.scss'
import ActionContextMenu from './ActionContextMenu'
import KnowledgeService from '@/services/KnowledgeService'
import { HelperService } from '@/services/helper.service'

const knowledgeService = new KnowledgeService()
const helperService = new HelperService()

interface LearningOutcomesProps {
  groupId: string
}

function LearningOutcomes(props: LearningOutcomesProps) {
  const { t } = useTranslation('translation')
  const history: any = useHistory()
  const [isLoadingItems, setIsLoadingItems] = useState(false)
  const [learningOutcomes, setLearningOutcomes] = useState([])
  const [isEditLearningOutcomes, setIsEditLearningOutcomes] = useState(false)

  // START Learning Outcomes Logic
  const fetchLos = async (groupId: string) => {
    setIsLoadingItems(true)
    const res = await knowledgeService.getLearningOutcomesByGroup(
      groupId,
      false,
    )
    setLearningOutcomes(res?.data?.data?.learning_outcomes)
    setIsLoadingItems(false)
  }

  useEffect(() => {
    fetchLos(props.groupId)
  }, [props])

  const handleChangeLo = (value: any, i: any) => {
    const copy = [...learningOutcomes]
    copy[i].text = value
    setLearningOutcomes(copy)
  }

  const handleRemoveLo = (i: any) => {
    const copy = [...learningOutcomes]
    copy.splice(i, 1)
    setLearningOutcomes(() => [...copy])
  }

  const renderLoInput = (value: any, i: any) => {
    return (
      <div className="learning-outcome-input">
        <input
          className="form-control border-0 p-0"
          defaultValue={value}
          value={value}
          placeholder={t('Input learning outcomes')}
          onChange={e => handleChangeLo(e.target.value, i)}
        />
        <div
          className="delete-outcome"
          role="button"
          onClick={() => handleRemoveLo(i)}
        >
          <b className="clr-pink">x</b>
        </div>
      </div>
    )
  }

  const handleSaveLos = async () => {
    if (learningOutcomes.length >= 0) {
      const outcomes: any = learningOutcomes
        .filter(o => o.text !== '')
        .map(o => ({
          learning_outcome_id: o._id,
          text: o?.text,
        }))
      try {
        const res = await knowledgeService.saveLearningOutcomesByGroup(
          props.groupId,
          outcomes,
        )
        setLearningOutcomes(res.data.data.learning_outcomes)
        helperService.showToaster(t('Success'), 'success')
        setIsEditLearningOutcomes(false)
      } catch (error) {
        helperService.showToaster('Error when save learning outcomes')
      }
    }
  }

  // END Learning Outcomes Logic

  return (
    <>
      <Drawer
        open={isEditLearningOutcomes}
        anchor="right"
        sx={{ width: '800px' }}
        onClose={() => setIsEditLearningOutcomes(false)}
      >
        <div className="tw-p-4 learning tw-w-[800px]">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <h3 className="font-16">{t('Edit Learning Outcomes')}</h3>
            <Button
              disabled={
                learningOutcomes.length > 0 && learningOutcomes[0].text === ''
              }
              onClick={() => {
                setLearningOutcomes([{ text: '' }, ...learningOutcomes])
              }}
              sx={{ ml: 'auto' }}
            >
              {t('+ Add')}
            </Button>
            <IconButton onClick={() => setIsEditLearningOutcomes(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <div className="">
            {learningOutcomes.length > 0 &&
              learningOutcomes.map((l: any, i: any) =>
                renderLoInput(l.text, i),
              )}
          </div>
          <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
            <Button variant="contained" onClick={() => handleSaveLos()}>
              {t('Save')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setIsEditLearningOutcomes(false)}
            >
              {t('Cancel')}
            </Button>
          </Stack>
        </div>
      </Drawer>
      <div className="learning">
        <div className="tw-flex tw-justify-end tw-items-center tw-mb-2">
          {learningOutcomes.length > 0 && (
            <Stack direction="row" justifyContent="flex-end" gap={2}>
              <Button
                onClick={() => {
                  history.push(
                    `${LEARNING_OUTCOMES_UPDATE}?group=${props.groupId}`,
                  )
                }}
                startIcon={<RefreshIcon fontSize="small" />}
              >
                {t('Update learning outcomes')}
              </Button>
            </Stack>
          )}
          <ActionContextMenu
            triggerText={t('+ Add')}
            menuItems={[
              {
                text: t('Create or Edit'),
                action: () => setIsEditLearningOutcomes(true),
              },
              {
                text: t('Create from Document'),
                action: () => {
                  history.push(
                    `/knowledge/setup/learning-outcomes` +
                      `?group=${props.groupId}`,
                  )
                },
              },
            ]}
          />
        </div>
        {learningOutcomes.length === 0 && (
          <div className="empty-knowledge text-center my-2">
            <h3 className="h6 clr-grey-txt7 mb-3">
              {t('You have yet to create any learning outcomes')}
            </h3>
            <figure className="h-auto">
              {isLoadingItems ? (
                <img src={loadingimg} alt="" />
              ) : (
                <img src={emptyKnowledge} alt="" />
              )}
            </figure>
            {!isLoadingItems && (
              <button
                className="btn btn-link m-2"
                onClick={() => {
                  history.push(
                    `/knowledge/setup/learning-outcomes` +
                      `?group=${props.groupId}`,
                  )
                }}
              >
                {t('Click here to get started')}
              </button>
            )}
          </div>
        )}
        {learningOutcomes.length > 0 && (
          <div>
            {learningOutcomes
              .filter((l: any) => l.text && l.text.trim() !== '')
              .map((l: any, i: number) => (
                <div className="card learning-outome-item mb-2" key={i}>
                  {l.text}
                </div>
              ))}
          </div>
        )}
      </div>
    </>
  )
}

export default LearningOutcomes
